// Right-hand main column for the /meet page. Switches between three views
// driven by root.meetView (list | call | transcript) — single /meet route
// architecture, no separate page files. VideoRoom and TranscriptPage are
// mount-controlled via `if:` so they get clean lifecycle hooks (onCreate
// connects LiveKit, onRemove disconnects).

// Pick the currently-focused room: explicit activeRoomId wins, otherwise
// fall back to the first row in root.rooms so landing on /meet without a
// hash shows real data (not a "pick a call" placeholder).
// Guard against rows with null ids — optimistic placeholders or partial
// realtime payloads can occasionally land in rooms[] before the real id
// arrives, and a fall-back to such a row produces #room=null URLs.
const _activeRoom = (root) => {
  const rooms = (root?.rooms || []).filter((r) => r && r.id)
  const activeId = root?.activeRoomId
  if (activeId) {
    const explicit = rooms.find((r) => r.id === activeId)
    if (explicit) return explicit
  }
  return rooms[0] || null
}

export const MeetMain = {
  extends: 'AppMain',
  padding: '0',

  Header: {
    extends: 'PageHeader',
    padding: 'A B',
    hide: (el, s) => s.root?.meetView === 'call',

    LeftGroup: {
      flow: 'y',
      align: 'flex-start left',
      gap: 'V',

      Title: {
        tag: 'h2',
        fontSize: 'B1',
        fontWeight: '600',
        margin: '0',
        color: 'title',
        text: (el, s) =>
          s.root?.meetView === 'transcript' ? 'Transcript' : 'Calls'
      },
      Caption: {
        tag: 'span',
        fontSize: 'Z2',
        color: 'caption',
        text: (el, s) => {
          if (s.root?.meetView === 'transcript') {
            const room = (s.root?.rooms || []).find(
              (r) => r.id === s.root?.activeRoomId
            )
            return room?.name || ''
          }
          const n = (s.root?.rooms || []).length
          return `${n} call${n === 1 ? '' : 's'}`
        }
      }
    },

    RightGroup: {
      hide: (el, s) => s.root?.meetView !== 'transcript',
      flow: 'x',
      gap: 'Z1',

      // End room from the transcript view — marks the DB row ended_at
      // (visible across the org via realtime) AND disconnects the local
      // LiveKit session if one's still up. Owner-only.
      EndRoomBtn: {
        hide: (el, s) => {
          const room = (s.root?.rooms || []).find(
            (r) => r.id === s.root?.activeRoomId
          )
          if (!room) return true
          if (room.endedAt) return true
          if (room.createdBy && room.createdBy !== s.root?.currentUserId)
            return true
          return false
        },
        tag: 'button',
        text: 'End room',
        padding: 'Z A',
        round: 'Z',
        borderWidth: '1px',
        borderColor: 'line',
        borderStyle: 'solid',
        background: 'transparent',
        color: 'caption',
        cursor: 'pointer',
        fontSize: 'Z2',
        onClick: async (e, el, s) => {
          await el.call('endRoom', s.root?.activeRoomId)
          await el.call('disconnectCall')
        }
      },

      BackBtn: {
        tag: 'button',
        text: '← Back to calls',
        padding: 'Z A',
        round: 'Z',
        borderWidth: '1px',
        borderColor: 'line',
        borderStyle: 'solid',
        background: 'transparent',
        color: 'title',
        cursor: 'pointer',
        fontSize: 'Z2',
        onClick: (e, el, s) => {
          s.root?.update({ meetView: 'list' })
          if (typeof window !== 'undefined') history.replaceState(null, '', '')
        }
      }
    }
  },

  Body: {
    extends: 'CommonCard',
    padding: '0',
    flex: '1 1 0',
    minHeight: '0',
    overflow: 'hidden',
    flow: 'y',

    // ── Sub-view: transcript ──────────────────────────────────────
    TranscriptView: {
      hide: (el, s) =>
        !(s.root?.meetView === 'transcript' && !!s.root?.activeRoomId),
      extends: 'MeetTranscriptPage'
    },

    // ── Sub-view: in-call ─────────────────────────────────────────
    // `if:` mount-control so onCreate/onRemove fire on view switch and
    // LiveKit Room state is created/destroyed cleanly.
    // `hide:` keeps the element mounted but invisible when inactive —
    // `if:` + `extends:` does NOT fire onCreate in DOMQL v3.14, so lifecycle
    // hooks on children are unreliable. LiveKit Room connect/disconnect
    // is driven from pages/meet.js at the page level instead, which DOES
    // fire onCreate/onUpdate reliably.
    CallView: {
      hide: (el, s) => !(s.root?.meetView === 'call' && !!s.root?.activeRoomId),
      extends: 'MeetVideoRoom'
    },

    // ── Sub-view: list (default) ──────────────────────────────────
    // When no activeRoomId is set (first landing), Detail falls back to
    // rooms[0] so the user sees real data instead of a "pick a call"
    // placeholder. Click handlers inside Detail read the same _activeRoom
    // helper and operate on whichever room is currently shown.
    ListView: {
      hide: (el, s) =>
        s.root?.meetView === 'call' || s.root?.meetView === 'transcript',
      flex: '1 1 0',
      minHeight: '0',
      overflow: 'auto',
      padding: 'B C',
      flow: 'y',
      gap: 'A',

      Empty: {
        hide: (el, s) => (s.root?.rooms || []).length > 0,
        flow: 'y',
        align: 'center center',
        gap: 'A',
        padding: 'D B',
        color: 'caption',
        fontSize: 'Z2',

        Title: {
          tag: 'h3',
          text: 'No calls yet',
          color: 'title',
          fontSize: 'B',
          margin: '0'
        },
        Caption: {
          tag: 'p',
          text: 'Click "+ New call" to start your first room.',
          margin: '0'
        }
      },

      Detail: {
        hide: (el, s) => !((s.root?.rooms || []).length > 0),
        flow: 'y',
        gap: 'Z1',
        padding: 'A',
        round: 'A',
        borderWidth: '1px',
        borderColor: 'line',
        borderStyle: 'solid',
        background: 'overlayStrong',

        Title: {
          tag: 'h3',
          fontSize: 'A',
          fontWeight: '600',
          color: 'title',
          margin: '0',
          text: (el, s) => _activeRoom(s.root)?.name || 'No calls yet'
        },

        Caption: {
          tag: 'p',
          fontSize: 'Z2',
          color: 'caption',
          margin: '0',
          hide: (el, s) => !!_activeRoom(s.root)?.endedAt,
          text: (el, s) => {
            const room = _activeRoom(s.root)
            if (!room) return ''
            const live = (s.root?.liveParticipants?.[room.id] || []).filter(
              (p) => !p.leftAt
            ).length
            const parts = []
            if (live > 0)
              parts.push(`${live} participant${live === 1 ? '' : 's'} live`)
            if (room.transcribeEnabled) parts.push('AI transcription on')
            if (room.isPrivate) parts.push('Private')
            return parts.join(' · ') || room.kind
          }
        },

        // Prominent banner shown when the room is ended.
        EndedNotice: {
          hide: (el, s) => !_activeRoom(s.root)?.endedAt,
          flow: 'x',
          align: 'center left',
          gap: 'Z',
          padding: 'Z A',
          round: 'Z',
          background: 'overlay',
          borderWidth: '1px',
          borderColor: 'line',
          borderStyle: 'solid',

          Icon: { icon: 'recordstop', boxSize: 'Z2' },
          Label: {
            tag: 'span',
            fontSize: 'Z2',
            color: 'caption',
            flex: '1',
            text: 'This room has ended'
          }
        },

        // Pending guests strip — owner-only. Visible in the list view so the
        // host knows guests are waiting even before joining the call.
        // Admit / Reject use the same MeetWaitingGuestRow registered component
        // as the in-call banner.
        WaitingSection: {
          hide: (el, s) => {
            const room = _activeRoom(s.root)
            if (!room) return true
            if (!room.createdBy || room.createdBy !== s.root?.currentUserId)
              return true
            const pending = (s.root?.waitingRoom?.[room.id] || []).filter(
              (g) => !g.admittedAt && !g.rejectedAt
            )
            return pending.length === 0
          },
          flow: 'y',
          gap: 'Z',
          padding: 'Z A',
          round: 'Z',
          background: 'overlay',
          borderWidth: '1px',
          borderColor: 'orange',
          borderStyle: 'solid',

          Header: {
            flow: 'x',
            align: 'center left',
            gap: 'V',
            Icon: { tag: 'span', text: '👋', fontSize: 'Z2' },
            Label: {
              tag: 'span',
              fontSize: 'Y2',
              fontWeight: '600',
              color: 'title',
              text: (el, s) => {
                const room = _activeRoom(s.root)
                const n = (s.root?.waitingRoom?.[room?.id] || []).filter(
                  (g) => !g.admittedAt && !g.rejectedAt
                ).length
                return `${n} guest${n === 1 ? '' : 's'} waiting`
              }
            }
          },

          GuestList: {
            flow: 'y',
            gap: 'V',
            childrenAs: 'state',
            children: (el, s) => {
              const room = _activeRoom(s.root)
              if (!room) return []
              return (s.root?.waitingRoom?.[room.id] || []).filter(
                (g) => !g.admittedAt && !g.rejectedAt
              )
            },
            childExtends: 'MeetWaitingGuestRow'
          }
        },

        Actions: {
          flow: 'x',
          gap: 'Z1',
          marginTop: 'Z',

          Join: {
            tag: 'button',
            text: 'Join call',
            padding: 'Z B',
            round: 'Z',
            border: 'none',
            theme: 'primary',
            cursor: 'pointer',
            fontSize: 'Z2',
            fontWeight: '600',
            hide: (el, s) => {
              const room = _activeRoom(s.root)
              if (!room) return true
              return !!room.endedAt
            },
            onClick: (e, el, s) => {
              const room = _activeRoom(s.root)
              if (!room) return
              s.root?.update({ activeRoomId: room.id, meetView: 'call' })
              if (typeof window !== 'undefined')
                history.replaceState(null, '', `#room=${room.id}&view=call`)
              el.call('connectCall', room.id)
            }
          },

          Transcript: {
            tag: 'button',
            text: 'View transcript',
            padding: 'Z B',
            round: 'Z',
            borderWidth: '1px',
            borderColor: 'line',
            borderStyle: 'solid',
            background: 'transparent',
            color: 'title',
            cursor: 'pointer',
            fontSize: 'Z2',
            hide: (el, s) => !_activeRoom(s.root)?.transcribeEnabled,
            onClick: (e, el, s) => {
              const room = _activeRoom(s.root)
              if (!room) return
              s.root?.update({ activeRoomId: room.id, meetView: 'transcript' })
              if (typeof window !== 'undefined')
                history.replaceState(
                  null,
                  '',
                  `#room=${room.id}&view=transcript`
                )
            }
          },

          // End / Reopen room — owner-only (RLS enforces). For ended rooms,
          // swap to a "Reopen" button so the same slot serves both states.
          // Also fully disconnects the local LiveKit session — ending a
          // room should log you out completely, not just switch state.
          // Settings — owner-only. Opens the room settings modal; writes
          // activeRoomId first so the modal seeds from the right room even
          // when Detail is falling back to rooms[0].
          SettingsBtn: {
            hide: (el, s) => {
              const room = _activeRoom(s.root)
              if (!room) return true
              if (room.createdBy && room.createdBy !== s.root?.currentUserId)
                return true
              return false
            },
            tag: 'button',
            text: 'Settings',
            padding: 'Z B',
            round: 'Z',
            borderWidth: '1px',
            borderColor: 'line',
            borderStyle: 'solid',
            background: 'transparent',
            color: 'title',
            cursor: 'pointer',
            fontSize: 'Z2',
            onClick: (e, el, s) => {
              const room = _activeRoom(s.root)
              if (!room) return
              s.root?.update({ activeRoomId: room.id, showRoomSettings: true })
            }
          },

          EndRoomBtn: {
            hide: (el, s) => {
              const room = _activeRoom(s.root)
              if (!room) return true
              if (room.endedAt) return true // ended → show Reopen instead
              if (room.createdBy && room.createdBy !== s.root?.currentUserId)
                return true
              return false
            },
            tag: 'button',
            text: 'End room',
            padding: 'Z B',
            round: 'Z',
            borderWidth: '1px',
            borderColor: 'red',
            borderStyle: 'solid',
            background: 'transparent',
            color: 'red',
            cursor: 'pointer',
            fontSize: 'Z2',
            fontWeight: '600',
            onClick: async (e, el, s) => {
              const room = _activeRoom(s.root)
              if (!room) return
              await el.call('endRoom', room.id)
              await el.call('disconnectCall')
            }
          },

          ReopenRoomBtn: {
            hide: (el, s) => {
              const room = _activeRoom(s.root)
              if (!room) return true
              if (!room.endedAt) return true
              if (room.createdBy && room.createdBy !== s.root?.currentUserId)
                return true
              return false
            },
            tag: 'button',
            text: 'Reopen',
            padding: 'Z B',
            round: 'Z',
            border: 'none',
            theme: 'secondary',
            cursor: 'pointer',
            fontSize: 'Z2',
            fontWeight: '600',
            onClick: async (e, el, s) => {
              const room = _activeRoom(s.root)
              if (room) await el.call('reopenRoom', room.id)
            }
          },

          // Recreate — visible to everyone when a room is ended. Creates a new
          // room with the same name and settings (fresh ID, clean state) and
          // immediately joins the call. Non-owners don't have Reopen, so this
          // gives them a quick path to continue the conversation.
          RecreateBtn: {
            hide: (el, s) => !_activeRoom(s.root)?.endedAt,
            tag: 'button',
            text: '+ New call',
            padding: 'Z B',
            round: 'Z',
            borderWidth: '1px',
            borderColor: 'line',
            borderStyle: 'solid',
            background: 'transparent',
            color: 'title',
            cursor: 'pointer',
            fontSize: 'Z2',
            onClick: async (e, el, s) => {
              const room = _activeRoom(s.root)
              if (!room) return
              const newId = await el.call('createRoom', {
                name: room.name,
                kind: room.kind,
                isPrivate: room.isPrivate,
                transcribeEnabled: room.transcribeEnabled
              })
              if (!newId) return
              s.root?.update({ activeRoomId: newId, meetView: 'call' })
              if (typeof window !== 'undefined')
                history.replaceState(null, '', `#room=${newId}&view=call`)
              el.call('connectCall', newId)
            }
          }
        }
      }
    }
  }
}
